import React, { useState } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import {
  Box,
  Typography,
  TextField,
  Snackbar,
  Link,
  IconButton,
  CircularProgress,
  Button,
  Card,
  CardContent,
  Divider,
  Avatar,
  Paper,
  Grid,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  VpnKey as VpnKeyIcon,
  ArrowForward as ArrowForwardIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Bugsnag from "@bugsnag/js";
import InputAdornment from "@mui/material/InputAdornment";
import { styled } from "@mui/material/styles";

// Removed unused imports - validation now handled by API
import NewColors from "../../themes/NewColors";
import logoImage from "../../assets/Logo.png";
import useEvents from "../../hooks/useEvents";

// Styled Components following Mighty45 branding patterns
const ConnectContainer = styled(Box)(({ theme }) => ({
  backgroundColor: NewColors.pageBackground,
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(3),
}));

const ConnectCard = styled(Card)(({ theme }) => ({
  maxWidth: 900,
  width: "100%",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
  backgroundColor: NewColors.cardBackground,
  overflow: "visible",
}));

const CardHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 4, 1, 4),
  textAlign: "center",
}));

const CardContentWrapper = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(0, 4, 3, 4),
  "&:last-child": {
    paddingBottom: theme.spacing(3),
  },
}));

const BrandTitle = styled(Typography)(({ theme }) => ({
  color: NewColors.text.primary,
  fontWeight: "bold",
  marginBottom: theme.spacing(0.5),
  fontFamily: "Lato, sans-serif",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: NewColors.text.primary,
  fontWeight: "bold",
  marginBottom: theme.spacing(1.5),
  fontFamily: "Lato, sans-serif",
  display: "flex",
  alignItems: "center",
  "& > svg": {
    marginRight: theme.spacing(1),
  },
}));

const FormField = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(1),
    backgroundColor: NewColors.sectionBackground,
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: NewColors.active.primary,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: NewColors.active.primary,
    },
  },
}));

const ConnectCodeTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(1),
    backgroundColor: NewColors.sectionBackground,
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: NewColors.active.primary,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: NewColors.active.primary,
    },
  },
  "& .MuiOutlinedInput-input": {
    textAlign: "center",
    fontSize: "1.5rem",
    fontWeight: 600,
    letterSpacing: "0.1rem",
  },
}));

const ConnectButton = styled(Button)(({ theme }) => ({
  backgroundColor: NewColors.active.primaryButton,
  color: NewColors.text.inverse,
  fontWeight: 600,
  fontSize: "1.1rem",
  padding: theme.spacing(1.5, 4),
  borderRadius: theme.spacing(1),
  textTransform: "none",
  boxShadow: `0 4px 12px rgba(196, 15, 13, 0.3)`,
  "&:hover": {
    backgroundColor: NewColors.status.error,
    boxShadow: `0 6px 16px rgba(196, 15, 13, 0.4)`,
  },
  "&:disabled": {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[500],
  },
  width: theme.spacing(30),
}));

const SectionIcon = styled(Avatar)(({ theme }) => ({
  backgroundColor: NewColors.active.primary,
  color: NewColors.text.inverse,
  width: theme.spacing(5),
  height: theme.spacing(5),
  marginRight: theme.spacing(1.5),
  boxShadow: "0 3px 10px rgba(196, 15, 13, 0.2)",
}));

const BenefitCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: NewColors.sectionBackground,
  borderRadius: theme.spacing(1),
  marginTop: theme.spacing(1.5),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
}));

const ConnectCodePage = () => {
  const navigate = useNavigate();
  const [connectCode, setConnectCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const validate = () => {
    if (email.trim().length <= 0) {
      return "Enter your email address to continue.";
    }

    if (password.trim().length <= 0) {
      return "You need a password to continue.";
    }

    if (connectCode.trim().length <= 0) {
      return "Enter the connect code provided by the organization owner to continue.";
    }

    return null;
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  async function handleOnboarding() {
    const validation = validate();
    if (validation) {
      showError(validation);
      return;
    }

    setIsCreating(true);

    try {
      // Call the atomic coach registration API
      const timestamp = Date.now().toString();
      const systemKey = 'M45_SYSTEM_KEY_2024';
      const signature = `${systemKey}_${timestamp}`;

      const response = await fetch("https://app.mighty45.com/api/v1/coach_register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-mighty45-system-key": systemKey,
          "x-mighty45-timestamp": timestamp,
          "x-mighty45-signature": signature,
        },
        body: JSON.stringify({
          email: email,
          password: password,
          connectCode: connectCode.trim(),
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Store user data in localStorage
        localStorage.setItem("uid", result.coach.uid);
        localStorage.setItem("organizationId", result.coach.organizationId);
        
        // Authenticate with Firebase using the created credentials
        try {
          await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL);
          await firebase.auth().signInWithEmailAndPassword(email, password);
          
          // Navigate to home page after successful authentication
          navigate("/home");
          setIsCreating(false);
        } catch (authError) {
          Bugsnag.notify(authError);
          showError("Coach registration successful, but there was an authentication issue. Please try signing in manually.");
          setIsCreating(false);
        }
      } else {
        showError(result.error || "That connect code is invalid. Please check with the organization's owner for a new connect code.");
        Bugsnag.notify(new Error(`Coach registration failed: ${result.error}`));
        setIsCreating(false);
      }
    } catch (e) {
      showError(e.message);
      Bugsnag.notify(e);
      setIsCreating(false);
    }
  }

  function showError(message) {
    setErrorMessage(message);
    setErrorOpen(true);
  }

  function clearError() {
    setErrorMessage("");
    setErrorOpen(false);
  }

  return (
    <ConnectContainer>
      <ConnectCard>
        <CardHeader>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
            <a href="https://mighty45.com">
              <img
                alt="Mighty45 Logo"
                style={{ maxWidth: "100%", height: "auto" }}
                width="120px"
                src={logoImage}
              />
            </a>
          </Box>
          <BrandTitle variant="h4">Join An Organization</BrandTitle>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Connect with your team using the code provided by the organization
            owner
          </Typography>
          <Divider sx={{ mt: 1, mb: 2 }} />
        </CardHeader>

        <CardContentWrapper>
          <Grid container spacing={2}>
            {/* Account Creation Section */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <SectionIcon>
                  <LockIcon />
                </SectionIcon>
                <SectionTitle variant="h5">Create Your Account</SectionTitle>
              </Box>

              <FormField>
                <StyledTextField
                  fullWidth
                  label="Email Address"
                  variant="outlined"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </FormField>

              <FormField>
                <StyledTextField
                  fullWidth
                  label="Password"
                  variant="outlined"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormField>
            </Grid>

            {/* Connect Code Section */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <SectionIcon>
                  <VpnKeyIcon />
                </SectionIcon>
                <SectionTitle variant="h5">Enter Connect Code</SectionTitle>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                Enter the code provided by your organization&apos;s
                administrator
              </Typography>

              <Paper
                elevation={0}
                sx={{
                  backgroundColor: NewColors.sectionBackground,
                  p: 2,
                  borderRadius: 2,
                }}
              >
                <ConnectCodeTextField
                  fullWidth
                  variant="outlined"
                  placeholder="EXAMPLE: ABC123"
                  value={connectCode}
                  onChange={(e) => setConnectCode(e.target.value)}
                />
              </Paper>

              <BenefitCard>
                <SecurityIcon sx={{ color: NewColors.active.primary }} />
                <Typography variant="body2">
                  Joining an organization gives you secure access to client data
                  and training plans
                </Typography>
              </BenefitCard>
            </Grid>
          </Grid>

          {/* Action Button */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <ConnectButton
              variant="contained"
              onClick={() => {
                handleOnboarding();
                useEvents().logButtonClick({
                  buttonName: "Join Organization",
                  pageName: "Connect Code",
                });
              }}
              endIcon={<ArrowForwardIcon />}
              disabled={isCreating}
            >
              Join Organization
            </ConnectButton>
          </Box>

          {isCreating && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mt: 2,
              }}
            >
              <CircularProgress
                size={36}
                sx={{ color: NewColors.brand.primary }}
              />
              <Typography variant="body2" sx={{ mt: 1, textAlign: "center" }}>
                Connecting you to the organization...
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Don&apos;t have a connect code? Contact the organization owner to
              get one.
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Already have an account?{" "}
              <Link href="/signin" color={NewColors.active.primary}>
                Sign in
              </Link>
            </Typography>
          </Box>
        </CardContentWrapper>
      </ConnectCard>

      <Snackbar
        open={errorOpen}
        autoHideDuration={6000}
        onClose={() => {
          clearError();
        }}
      >
        <Alert
          onClose={() => {
            clearError();
          }}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </ConnectContainer>
  );
};

export default ConnectCodePage;
